import { useEffect, useRef, useState } from 'react';
import type { LFOWaveform } from '../core/identity/ProfileTypes';
import { useReducedMotion } from './useReducedMotion';

/**
 * useLFO - Low-Frequency Oscillator hook used by <ProfilePreview> as the
 * hue / color-modulation source for the new "Section 5" animated card
 * effects (Sinewave, Sawtooth, Square/Pulse, Sample & Hold).
 *
 * Returned normalized value is in [-1, 1].
 *   - Sine:     smooth oscillation
 *   - Sawtooth: linear ramp from -1 -> 1 with abrupt reset
 *   - Pulse:    +/-1 square wave at 50% duty cycle
 *   - S&H:      random step held for (1/rate) seconds
 *
 * Drives off requestAnimationFrame so it auto-pauses when the tab is
 * hidden. Falls back to a setInterval if rAF is unavailable (ancient
 * browsers / test environments).
 */
export function useLFO(
  waveform: LFOWaveform | undefined,
  rate: number | undefined,
  frequency: number | undefined,
): number {
  const r = typeof rate === 'number' && rate > 0 ? rate : 1.0;
  const f = typeof frequency === 'number' && frequency > 0 ? frequency : 1.0;
  const w = waveform ?? 'None';

  const [value, setValue] = useState(0);
  const startRef = useRef<number>(typeof performance !== 'undefined' ? performance.now() : Date.now());
  const shRef = useRef<{ heldAt: number; value: number; timer: ReturnType<typeof setTimeout> | null }>({
    heldAt: 0,
    value: 0,
    timer: null,
  });
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (w === 'None') {
      setValue(0);
      return;
    }

    // Respect prefers-reduced-motion: hold a static value (0) instead of
    // spinning rAF indefinitely. Background animations on the profile
    // card (LFO opacity / hue modulation) are the primary consumer.
    if (reducedMotion) {
      setValue(0);
      return;
    }

    startRef.current = typeof performance !== 'undefined' ? performance.now() : Date.now();

    // Sample & Hold runs as a discrete scheduler separate from rAF.
    if (w === 'S&H') {
      const tick = () => {
        shRef.current.value = Math.random() * 2 - 1;
        shRef.current.heldAt = Date.now();
        setValue(shRef.current.value);
      };
      tick();
      const intervalMs = Math.max(60, 1000 / r);
      const handle = setInterval(tick, intervalMs);
      return () => clearInterval(handle);
    }

    let raf = 0;
    let frameCounter = 0;
    const loop = () => {
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const elapsed = (now - startRef.current) / 1000; // seconds
      const phase = elapsed * r * f; // unitless cycles-since-start
      let out = 0;
      switch (w) {
        case 'Sine':
          out = Math.sin(phase * 2 * Math.PI);
          break;
        case 'Sawtooth':
          out = (phase % 1) * 2 - 1;
          break;
        case 'Pulse':
          out = (phase % 1) < 0.5 ? 1 : -1;
          break;
        default:
          out = 0;
      }
      // Throttle setState to ~30fps by skipping every other frame
      if ((frameCounter & 1) === 0) {
        setValue(out);
      }
      frameCounter++;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [w, r, f, reducedMotion]);

  return value;
}
